import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { PermissionGuardSome } from './core/auth/guards/permission.guard';
import { DriverGuard } from './core/auth/guards/driver.guard';
import { DriverEntryRedirectGuard } from './core/auth/guards/driver-entry-redirect.guard';
import { RequesterGuard } from './core/auth/guards/requester.guard';
import { RequesterServicesRedirectGuard } from './core/auth/guards/requester-services-redirect.guard';
import { AdminGuard } from './core/auth/guards/admin.guard';
import { AdminDefaultRedirectGuard } from './core/auth/guards/admin-default-redirect.guard';
import { AccessApprovalGuard } from './core/auth/guards/access-approval.guard';
// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'home' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'not-found-company', loadChildren: () => import('app/modules/auth/not-found-company/not-found-company.routes') },
    {
        path: 'access-approval',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        loadChildren: () => import('app/modules/access-approval/access-approval.routes'),
    },
    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') },
            { path: 'sign-up-type', loadChildren: () => import('app/modules/auth/sign-up-type/sign-up-type.routes') },
            { path: 'cadastro-medico', loadChildren: () => import('app/modules/auth/sign-up-medic/sign-up-medic.routes') },
            { path: 'sign-up-medic-success', loadChildren: () => import('app/modules/auth/sign-up-medic-success/sign-up-medic-success.routes') },
        ],
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            {
                path: 'module-entry',
                loadChildren: () => import('app/modules/admin-module-entry/admin-module-entry.routes'),
            },
            { path: 'change-password', loadChildren: () => import('app/modules/auth/change-password/change-password.routes') },
            // { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') },
        ],
    },
    // Admin routes
    {
        path: 'home',
        canActivate: [AuthGuard, DriverEntryRedirectGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            // type: ['USER'],
            // permissions: ['pcp_enabled'],
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/home/home.routes'),
            },
        ],
    },
    {
        path: 'ambulance/:id',
        pathMatch: 'full',
        redirectTo: 'cockpit/:id',
    },
    {
        path: 'company-cockpit',
        pathMatch: 'prefix',
        redirectTo: 'allocation-engine',
    },
    {
        path: 'allocation-engine',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            // Permissão dedicada ao Cockpit da Empresa. O endpoint
            // `GET /service/company-cockpit` exige a mesma `COMPANY_COCKPIT_VIEW`
            // no backend.
            permissions: ['COMPANY_COCKPIT_VIEW'],
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/company-cockpit/allocation-engine/allocation-engine.component').then(
                        (c) => c.AllocationEngineComponent,
                    ),
            },
            {
                path: 'bulk-schedule',
                loadComponent: () =>
                    import('app/modules/company-cockpit/bulk-schedule-page/bulk-schedule-page.component').then(
                        (c) => c.BulkSchedulePageComponent,
                    ),
            },
            {
                path: 'timeline',
                loadComponent: () =>
                    import('app/modules/company-cockpit/cockpit-timeline-page/cockpit-timeline-page.component').then(
                        (c) => c.CockpitTimelinePageComponent,
                    ),
                data: {
                    // Página dedicada para abrir em nova aba: sem header e sem sidebar.
                    layout: 'empty',
                },
            },
        ],
    },
    {
        path: 'cockpit',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            // type: ['USER'],
            // permissions: ['pcp_enabled'],
        },
        children: [
            {
                path: ':id',
                loadComponent: () => import('app/modules/ambulance/ambulance-detail/ambulance-detail.component').then((c) => c.AmbulanceDetailComponent),
            },
        ],
    },
    {
        path: 'cabin',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: ':id',
                loadComponent: () => import('app/modules/cabin/cabin-detail/cabin-detail.component').then((c) => c.CabinDetailComponent),
            },
        ],
    },
    // Rota para o player de vídeo standalone (Task #1253)
    {
        path: 'video-player',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: '',
                loadComponent: () => import('app/modules/video-player/video-player-page.component').then((c) => c.VideoPlayerPageComponent),
            },
        ],
    },
    {
        path: 'acompanhar/:token',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/service/service-share-page/service-share-page.component').then(
                        (c) => c.ServiceSharePageComponent,
                    ),
            },
        ],
    },
    {
        path: 'teleconsulta/:token',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/teleconsultation/public-page/teleconsultation-public-page.component').then(
                        (c) => c.TeleconsultationPublicPageComponent,
                    ),
            },
        ],
    },
    {
        path: 'budget',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/pricing/public-page/pricing-public-page.component').then(
                        (c) => c.PricingPublicPageComponent,
                    ),
            },
        ],
    },
    {
        path: 'pricing',
        redirectTo: 'budget',
        pathMatch: 'full',
    },
    {
        path: 'orcamento',
        redirectTo: 'budget',
        pathMatch: 'full',
    },
    {
        path: 'vehicles-map',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'empty',
            permissions: ['VEHICLE_VIEW'],
        },
        children: [
            {
                path: '',
                loadComponent: () =>
                    import('app/modules/vehicles/vehicle/map/vehicle-fleet-map.component').then((c) => c.VehicleFleetMapComponent),
            },
        ],
    },
    {
        path: 'patients',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            // permissions: ['pcp_enabled', 'can_write_clients'],
            config_screen: {
                type: 'PATIENT',
            },
            showSearch: true,
        },
        loadChildren: () => import('app/modules/configurations/access/user/user.routes'),
    },
    {
        path: 'tickets',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            permissions: ['TICKET_VIEW'],
        },
        loadChildren: () => import('app/modules/tickets/ticket.routes'),
    },
    {
        path: 'configuration',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            // type: ['USER'],
            // permissions: ['pcp_enabled'],
        },
        children: [
            {
                path: 'services',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: {
                    permissions: ['TYPE_SERVICE_VIEW'],
                },
                loadChildren: () => import('app/modules/configurations/services/services.routes'),
            },
        ],
    },
    {
        path: 'users',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            showSearch: true,
        },
        loadChildren: () => import('app/modules/configurations/access/user/user-by-role.routes'),
    },
    {
        path: 'customer',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        loadChildren: () => import('app/modules/customer/customer.routes'),
    },
    {
        path: 'calls',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            permissions: ['CALL_VIEW'],
        },
        loadChildren: () => import('app/modules/configurations/calls/calls.routes'),
    },
    {
        path: 'reports/service',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            permissions: ['SERVICE_REPORT_SERVICE_VIEW'],
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/configurations/reports/service/service-report-dashboard.routes'),
            },
        ],
    },
    {
        path: 'reports/fleet',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            permissions: ['SERVICE_REPORT_FLEET_VIEW'],
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/configurations/reports/fleet/fleet-report-dashboard.routes'),
            },
        ],
    },
    {
        path: 'vehicles',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            permissions: ['VEHICLE_VIEW'],
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/vehicles/vehicle/vehicle.routes'),
            },
        ],
    },
    {
        path: 'cabins',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            permissions: ['CABIN_VIEW', 'CABIN_CREATE', 'CABIN_UPDATE', 'CABIN_DELETE'],
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/cabin/cabin.routes'),
            },
        ],
    },
    {
        path: 'shifts',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            permissions: ['SHIFT_VIEW'],
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/shifts/shifts.routes'),
            },
        ],
    },
    {
        path: 'pharmacy',
        canActivate: [AuthGuard, AccessApprovalGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            permissions: ['SUPPLY_VIEW'],
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/pharmacy/pharmacy.routes'),
            },
        ],
    },
    {
        path: 'services',
        canActivate: [AuthGuard, AccessApprovalGuard, RequesterServicesRedirectGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, RequesterServicesRedirectGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            permissions: ['SERVICE_VIEW'],
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/service/service.routes'),
            },
        ],
    },
    {
        path: 'driver',
        canActivate: [AuthGuard, AccessApprovalGuard, DriverGuard],
        canActivateChild: [AuthGuard, DriverGuard],
        component: LayoutComponent,
        data: {
            layout: 'driver',
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/driver-app/driver.routes'),
            },
        ],
    },
    {
        path: 'requester',
        canActivate: [AuthGuard, AccessApprovalGuard, RequesterGuard, PermissionGuardSome],
        canActivateChild: [AuthGuard, RequesterGuard, PermissionGuardSome],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'classic',
            // Mesmas regras de atendimento: portal requester exige SERVICE_VIEW no grupo.
            permissions: ['SERVICE_VIEW'],
        },
        children: [
            {
                path: '',
                loadChildren: () => import('app/modules/requester-app/requester.routes'),
            },
        ],
    },
    {
        path: 'admin',
        canActivate: [AuthGuard, AccessApprovalGuard, AdminGuard],
        canActivateChild: [AuthGuard, AdminGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        data: {
            layout: 'classic',
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                canActivate: [AdminDefaultRedirectGuard],
                children: [],
            },
            {
                path: 'home',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_HOME_VIEW' },
                loadChildren: () => import('app/modules/configurations/totals-kpi/totals-kpi.routes'),
            },
            {
                path: 'vehicles-global-map',
                canActivate: [PermissionGuardSome],
                data: { permissions: 'ADMIN_HOME_VIEW' },
                loadComponent: () =>
                    import('app/modules/vehicles/vehicle/map/vehicle-global-map.component').then((c) => c.VehicleGlobalMapComponent),
            },
            // Cadastros
            {
                path: 'access/companies',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_COMPANY_VIEW' },
                loadChildren: () => import('app/modules/configurations/access/company/company.routes'),
            },
            {
                path: 'access/requesters',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_REQUESTER_VIEW' },
                loadChildren: () => import('app/modules/configurations/access/requester/requester.routes'),
            },
            {
                path: 'access/admins',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_ADMIN_USER_VIEW' },
                loadChildren: () => import('app/modules/configurations/access/user/user-admins.routes'),
            },
            {
                path: 'access/users-no-company',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_PROFESSIONAL_VIEW' },
                loadChildren: () => import('app/modules/configurations/access/user/user-no-company.routes'),
            },
            {
                path: 'access/regulators',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_REGULATOR_VIEW' },
                loadChildren: () => import('app/modules/configurations/access/user/user-regulators.routes'),
            },
            {
                path: 'access/providers',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_PROVIDER_VIEW' },
                loadChildren: () => import('app/modules/configurations/access/provider/provider.routes'),
            },
            {
                path: 'access/company-activities',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_COMPANY_VIEW' },
                loadChildren: () => import('app/modules/configurations/access/company_activity/company_activity.routes'),
            },
            {
                path: 'access/permission-groups',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_PERMISSION_GROUP_VIEW' },
                loadChildren: () => import('app/modules/configurations/access/permission-group/permission-group.routes'),
            },
            {
                path: 'access/api-keys',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_API_KEY_VIEW' },
                loadChildren: () => import('app/modules/configurations/access/api-key/api-key.routes'),
            },
            {
                path: 'vehicle-plans',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_VEHICLE_PLAN_VIEW' },
                loadChildren: () => import('app/modules/configurations/vehicle-plan/vehicle-plan.routes'),
            },
            // Atendimentos (catálogos globais)
            {
                path: 'services/type_service',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_TYPE_SERVICE_VIEW' },
                loadChildren: () => import('app/modules/configurations/services/type_service/type_service.routes'),
            },
            {
                path: 'services/personal_background',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_PERSONAL_BACKGROUND_VIEW' },
                loadChildren: () => import('app/modules/configurations/services/personal_background/personal_background.routes'),
            },
            {
                path: 'services/scale',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_SCALE_VIEW' },
                loadChildren: () => import('app/modules/configurations/services/scale/scale.routes'),
            },
            {
                path: 'services/health_plan',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_HEALTH_PLAN_VIEW' },
                loadChildren: () => import('app/modules/configurations/services/health_plan/health_plan.routes'),
            },
            {
                path: 'services/cnes_establishment',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_CNES_VIEW' },
                loadChildren: () => import('app/modules/configurations/services/cnes_establishment/cnes-establishment.routes'),
            },
            {
                path: 'services/medical_record',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_MEDICAL_RECORD_VIEW' },
                loadChildren: () => import('app/modules/configurations/services/medical_record/medical_record-admin.routes'),
            },
            // Equipamentos
            {
                path: 'equipment',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_EQUIPMENT_VIEW' },
                loadChildren: () => import('app/modules/configurations/equipamentos/equipamentos.routes'),
            },
            {
                path: 'indicators/indicator',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_EQUIPMENT_VIEW' },
                loadChildren: () => import('app/modules/configurations/indicators/indicator/indicator.routes'),
            },
            {
                path: 'indicators/measure_equipment',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_EQUIPMENT_VIEW' },
                loadChildren: () => import('app/modules/configurations/indicators/measure_equipment/measure_equipment.routes'),
            },
            {
                path: 'indicators/type_measure_equipment',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_EQUIPMENT_VIEW' },
                loadChildren: () => import('app/modules/configurations/indicators/type_measure_equipment/type_measure_equipment.routes'),
            },
            {
                path: 'video_cameras/video_equipment',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_EQUIPMENT_VIEW' },
                loadChildren: () => import('app/modules/configurations/video_cameras/video_equipment/video_equipment.routes'),
            },
            {
                path: 'routers',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_EQUIPMENT_VIEW' },
                loadChildren: () => import('app/modules/configurations/routers/routers.routes'),
            },
            {
                path: 'starlink',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_EQUIPMENT_VIEW' },
                loadChildren: () => import('app/modules/configurations/starlink/starlink.routes'),
            },
            {
                path: 'sim_card',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_EQUIPMENT_VIEW' },
                loadChildren: () => import('app/modules/configurations/sim_card/sim_card.routes'),
            },
            // Outros
            {
                path: 'contracts',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_INTEGRATION_VIEW' },
                loadChildren: () => import('app/modules/configurations/contract-type/contract-type.routes'),
            },
            {
                path: 'integrations',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_INTEGRATION_VIEW' },
                loadChildren: () => import('app/modules/configurations/integration/integration.routes'),
            },
            {
                path: 'crm',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_CRM_CALLS_VIEW' },
                loadChildren: () => import('app/modules/admin/crm/crm.routes'),
            },
            {
                path: 'prompts',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_PROMPT_VIEW' },
                loadChildren: () => import('app/modules/configurations/prompts/prompts.routes'),
            },
            {
                path: 'news',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_NEWS_VIEW' },
                loadChildren: () => import('app/modules/configurations/news/news.routes'),
            },
            {
                path: 'pricing',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_PRICING_VIEW' },
                loadChildren: () => import('app/modules/configurations/pricing/pricing.routes'),
            },
            {
                path: 'supply',
                canActivate: [PermissionGuardSome],
                canActivateChild: [PermissionGuardSome],
                data: { permissions: 'ADMIN_SUPPLY_CATALOG_VIEW' },
                loadChildren: () => import('app/modules/configurations/supply/supply.routes'),
            },
        ],
    },
];
